// Máscara de valor em pt-BR: milhar com ".", decimal com ",".
//
// Regra do módulo: o ESTADO é sempre CANÔNICO — string com ponto decimal
// ("240927.59"), que é o que `Number()` e o payload da API consomem. A
// máscara vive só na exibição e na entrada; nada aqui muda o que é gravado.
//
// Digitação e colagem têm regras OPOSTAS quanto ao ponto, e é por isso que
// são duas funções de entrada e não uma:
//   - digitando, o texto que chega no onChange já vem formatado da própria
//     tela ("240.927" + tecla "5" = "240.9275"), então ponto é SEMPRE milhar;
//   - colando, o texto vem cru do clipboard e pode estar em qualquer formato
//     (planilha em pt-BR ou en-US), então o separador precisa ser deduzido.
// Aplicar a heurística da colagem no onChange quebraria a digitação.

const CASAS_PADRAO = 2;

// Canônico -> tela. Preserva a vírgula solta ("240.927,") e as casas
// digitadas até aqui: é o que impede a máscara de engolir a tecla da vírgula
// e o primeiro decimal enquanto o usuário ainda está digitando.
export function formatarValorBR(canonico) {
  if (canonico === "" || canonico == null) return "";
  const s = String(canonico);
  const ponto = s.indexOf(".");
  const inteiro = ponto === -1 ? s : s.slice(0, ponto);
  const decimal = ponto === -1 ? null : s.slice(ponto + 1);
  const n = parseInt(inteiro, 10);
  if (isNaN(n) && decimal === null) return "";
  const inteiroFmt = isNaN(n) ? "0" : n.toLocaleString("pt-BR");
  return decimal === null ? inteiroFmt : `${inteiroFmt},${decimal}`;
}

// Tela -> canônico, no onChange. Ponto é milhar (descartado), vírgula é
// decimal. O sinal negativo é descartado de propósito: em todos os campos que
// usam esta máscara o sinal vem do toggle Venda/Compra ou da regra da linha,
// nunca do que foi digitado (PosicaoDia.js: saveRow, saveAjuste, saveAjuste2 e
// recebimentoCalc reaplicam o sinal por conta própria).
export function parsearValorBR(texto, { decimais = CASAS_PADRAO } = {}) {
  const limpo = String(texto ?? "").replace(/\./g, "").replace(/[^\d,]/g, "");
  if (!limpo) return "";
  const virgula = limpo.indexOf(",");
  if (virgula === -1) {
    const n = parseInt(limpo, 10);
    return isNaN(n) ? "" : String(n);
  }
  const inteiro = parseInt(limpo.slice(0, virgula), 10);
  let decimal = limpo.slice(virgula + 1).replace(/,/g, "");
  if (decimais != null) decimal = decimal.slice(0, decimais);
  return `${isNaN(inteiro) ? "0" : String(inteiro)}.${decimal}`;
}

// Clipboard -> canônico, no onPaste. Devolve null quando não há nada
// numérico aproveitável, e nesse caso o chamador deve deixar o onChange
// padrão tratar.
//
// Heurística do separador decimal:
//   - os dois presentes  -> o ÚLTIMO é o decimal ("240.927,59", "1,129,191.90")
//   - um só, repetido    -> milhar ("1.129.191")
//   - um só, uma vez, com exatamente 3 dígitos depois -> milhar ("240.927")
//   - um só, uma vez, com 1, 2 ou 4+ dígitos depois   -> decimal ("240927.59")
//
// A 3ª regra é a única ambígua: "240.927" vira 240927 (leitura pt-BR). É o
// motivo de esta máscara ficar restrita a campos de MONTANTE — num campo de
// taxa, colar "4.930" viraria 4930.
export function parsearValorColado(texto, { decimais = CASAS_PADRAO } = {}) {
  // Fora R$/US$, espaço comum e NBSP (planilha), letras e o sinal.
  const num = String(texto ?? "").replace(/[^\d.,]/g, "");
  if (!/\d/.test(num)) return null;

  const ultVirgula = num.lastIndexOf(",");
  const ultPonto = num.lastIndexOf(".");
  let sep;
  if (ultVirgula !== -1 && ultPonto !== -1) {
    sep = Math.max(ultVirgula, ultPonto);
  } else {
    const s = ultVirgula !== -1 ? ultVirgula : ultPonto;
    if (s === -1) {
      sep = -1;
    } else {
      const ocorrencias = num.split(num[s]).length - 1;
      const cauda = num.length - s - 1;
      sep = (ocorrencias > 1 || cauda === 3) ? -1 : s;
    }
  }

  const inteiro = (sep === -1 ? num : num.slice(0, sep)).replace(/[.,]/g, "");
  let decimal = sep === -1 ? "" : num.slice(sep + 1).replace(/[.,]/g, "");
  if (decimais != null) decimal = decimal.slice(0, decimais);
  const n = parseInt(inteiro || "0", 10);
  if (isNaN(n)) return null;
  return decimal ? `${n}.${decimal}` : String(n);
}
